import { useMemo } from 'react';
import useApi from './useApi';
import { PRICING_FALLBACK } from '../data/pricingFallback';

export function formatPrice(cents) {
  if (cents === null || cents === undefined) return null;
  return '$' + (cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

// Human price line for a plan: "$1,500/mo", "$1,000", "from $100", "Custom".
export function priceLine(plan) {
  if (plan.priceLabel && (plan.billing === 'custom' || plan.priceCents == null)) return plan.priceLabel;
  const base = formatPrice(plan.priceCents);
  if (!base) return plan.priceLabel || 'Custom';
  if (plan.billing === 'monthly') return base + '/mo';
  if (plan.billing === 'percent') return plan.priceLabel || base + '/mo';
  return base;
}

export function depositLine(plan, depositPercent = 50) {
  if (plan.billing !== 'one-time' || plan.priceCents == null) return null;
  const pct = plan.depositPercent == null ? depositPercent : plan.depositPercent;
  if (pct >= 100) return null;
  return formatPrice(Math.round((plan.priceCents * pct) / 100)) + ' to start';
}

// Public pricing catalog with a bundled fallback so the site renders even if
// the API is unreachable (static hosting, cold VPS, etc.).
export default function usePricing() {
  const { data, error, isLoading } = useApi('/api/pricing');
  return useMemo(() => {
    const live = data && data.data && Array.isArray(data.data.plans) ? data.data : null;
    const catalog = live || PRICING_FALLBACK;
    const plans = catalog.plans.filter((p) => p.active !== false).slice().sort((a, b) => a.sort - b.sort);
    const groups = catalog.groups
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((g) => ({ ...g, plans: plans.filter((p) => p.group === g.id) }))
      .filter((g) => g.plans.length);
    const byId = Object.fromEntries(plans.map((p) => [p.id, p]));
    return {
      plans,
      groups,
      byId,
      meta: catalog.meta || { depositPercent: 50, checkoutEnabled: false },
      isLive: Boolean(live),
      isLoading,
      error,
    };
  }, [data, error, isLoading]);
}
